
import React from "react";
import { useTranslations } from "use-intl";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { CheckCircle } from "lucide-react";
import { Invoice } from "@/types/payments";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import SekEquivalent from "@/components/shared/SekEquivalent";
import { useFormatters } from "@financialharmony/ui";
import { useFormatOptionalDate } from "@/i18n/useFormatOptionalDate";

interface ApprovalsInvoicesTableProps {
  invoices: Invoice[];
  selectedInvoices: string[];
  toggleSelectInvoice: (invoiceId: string) => void;
  toggleSelectAll: (invoices: Invoice[]) => void;
  handleSort: (key: keyof Invoice) => void;
  renderSortIcon: (key: keyof Invoice) => React.ReactNode;
  handleApprove: (invoiceId: string) => void;
  formatCurrency: (amount: number, currency: string) => string;
  isPendingTab: boolean;
}

const ApprovalsInvoicesTable: React.FC<ApprovalsInvoicesTableProps> = ({
  invoices,
  selectedInvoices,
  toggleSelectInvoice,
  toggleSelectAll,
  handleSort,
  renderSortIcon,
  handleApprove,
  formatCurrency,
  isPendingTab
}) => {
  const t = useTranslations("consultantApprovals.table");
  const { formatDate } = useFormatters();
  const formatOptionalDate = useFormatOptionalDate();
  return (
    <TooltipProvider>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {isPendingTab && (
                <TableHead className="w-12">
                  <Checkbox 
                    checked={selectedInvoices.length === invoices.length && invoices.length > 0}
                    onCheckedChange={() => toggleSelectAll(invoices)}
                    aria-label={t("selectAll")}
                  />
                </TableHead>
              )}
              <TableHead className="cursor-pointer" onClick={() => handleSort('id')}>
                {t("columns.id")}
                {renderSortIcon('id')}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('vendor')}>
                {t("columns.vendor")}
                {renderSortIcon('vendor')}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('amount')}>
                {t("columns.amount")}
                {renderSortIcon('amount')}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('currency')}>
                {t("columns.currency")}
                {renderSortIcon('currency')}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('dueDate')}>
                {t("columns.dueDate")}
                {renderSortIcon('dueDate')}
              </TableHead>
              {isPendingTab ? <TableHead>{t("columns.actions")}</TableHead> : <TableHead>{t("columns.status")}</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map(invoice => (
              <TableRow key={invoice.id}>
                {isPendingTab && (
                  <TableCell>
                    <Checkbox 
                      checked={selectedInvoices.includes(invoice.id)}
                      onCheckedChange={() => toggleSelectInvoice(invoice.id)}
                      aria-label={t("selectInvoice", { id: invoice.id })} 
                    />
                  </TableCell>
                )}
                <TableCell>{invoice.id}</TableCell>
                <TableCell>{invoice.vendor}</TableCell>
                <TableCell>
                  {formatCurrency(invoice.amount, invoice.currency)}
                  <SekEquivalent sekAmount={invoice.sekAmount} currency={invoice.currency} />
                </TableCell>
                <TableCell>{invoice.currency}</TableCell>
                <TableCell>{formatOptionalDate(invoice.dueDate)}</TableCell>
                <TableCell>
                  {isPendingTab ? (
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="flex items-center gap-2" 
                      onClick={() => handleApprove(invoice.id)}
                    >
                      <CheckCircle className="h-4 w-4" />
                      {t("approve")}
                    </Button>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 flex w-fit items-center gap-1 cursor-help">
                          <CheckCircle className="h-3 w-3" />
                          {t("approved")}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="space-y-1 text-sm">
                          <p><strong>{t("approvedBy")}</strong> {invoice.approvedBy || t("unknown")}</p>
                          <p><strong>{t("approvedOn")}</strong> {invoice.approvedDate ? formatDate(invoice.approvedDate, "numeric") : t("unknownDate")}</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {invoices.length === 0 && (
              <TableRow>
                <TableCell colSpan={isPendingTab ? 7 : 6} className="h-24 text-center">
                  {t(isPendingTab ? "emptyPending" : "emptyApproved")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  );
};

export default ApprovalsInvoicesTable;
